/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { useCallback, useEffect, useState } from "react";

import { useAuth0 } from "@auth0/auth0-react";
import {
  Space,
  Input,
  Radio,
  Typography,
  Col,
  message,
  Row,
  Button,
  Card,
  RadioChangeEvent,
} from "antd";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import queryString from "query-string";

import Form, { Item, useForm } from "./Form";
import commonStyles from "./style/common.module.scss";

import styles from "./style/ResetPasswordScreen.module.scss";

import { useTranslation } from "react-i18next";
import { UsersApi } from "client";
import cx from "classnames";
import { ErrorResponse, getErrorResponse } from "utils/Api";
import { useApiWithTimeout } from "hooks/ApiWrapperWithTimeout";
import { Const } from "const";
import { SpinnerDelay } from "components/Common/SpinnerDelay";

const { Text, Title } = Typography;

export interface LoginHelpForm {
  actionSelection: string;
  loginEmail: string;
}

const LoginHelpActionsEnum = {
  passwordReset: 0,
  unlockAccount: 1,
};

const LoginHelpEmailErrorCode = {
  notFound: "UI-100040038",
  notLocked: "UI-100120040",
};

const ResetPasswordScreen = () => {
  const { t } = useTranslation();
  const { loginWithRedirect } = useAuth0();
  const [form] = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isPC = useBreakpoint().lg;

  const [selectValue, setSelectValue] = useState(
    LoginHelpActionsEnum.passwordReset
  );

  const [autoFocus, setAutoFocus] = useState(true);
  const [usersApi, cancelUsersApi] = useApiWithTimeout(
    UsersApi,
    Const.operationTimeout.usersTask,
    false
  );

  const queryParams = queryString.parse(window.location.search);
  const { token, email } = queryParams;

  const LoginHelpMessage = {
    success: t("AWVUI-MSG153"),
    error: t("AWVUI-MSG152"),
    notFound: t("AWVUI-MSG151"),
    notLocked: t("AWVUI-MSG154"),
  };

  /**
   * convert (string | (string | null)[] | null) to string
   * @param {string | (string | null)[] | null} value input
   * @return {string} output
   */
  const convertToString = (
    value: string | (string | null)[] | null
  ): string => {
    if (typeof value === "string") {
      return value;
    } else if (Array.isArray(value)) {
      return value.filter((item) => typeof item === "string").join("");
    } else {
      return "";
    }
  };

  const unlockAccountWithEmailToken = useCallback(async () => {
    // click email link
    const emailString = convertToString(email);
    const tokenString = convertToString(token);
    if (token && email) {
      setIsSubmitting(true);
      try {
        await usersApi.unlockAccount({
          email: emailString,
          token: tokenString,
        });
        // succeed
        loginWithRedirect();
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") {
          // Ignore abort errors
          console.debug("ignore");
        } else {
          // failed
          message.error(LoginHelpMessage.error);
        }
      } finally {
        setIsSubmitting(false);
        setAutoFocus(false);
      }
    }
  }, [LoginHelpMessage.error, email, loginWithRedirect, token, usersApi]);

  useEffect(() => {
    unlockAccountWithEmailToken();
  }, [unlockAccountWithEmailToken]);

  const handleBack = useCallback(() => {
    loginWithRedirect();
  }, [loginWithRedirect]);

  const handleFinish = async (values: LoginHelpForm) => {
    setIsSubmitting(true);
    const requestBody = {
      email: values.loginEmail,
    };
    if (selectValue === LoginHelpActionsEnum.passwordReset) {
      try {
        await usersApi.resetPassword(requestBody);
        // succeed
        message.success(LoginHelpMessage.success);
        form.resetFields(["loginEmail"]);
      } catch (error: unknown) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          // failed
          const errorResponse = await getErrorResponse(error);
          message.error(getPasswordResetErrorMsg(errorResponse));
        }
      }
    } else if (selectValue === LoginHelpActionsEnum.unlockAccount) {
      try {
        await usersApi.sendUnlockAccountMail(requestBody);
        // succeed
        message.success(LoginHelpMessage.success);
        form.resetFields(["loginEmail"]);
      } catch (error: unknown) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          // failed
          const errorResponse = await getErrorResponse(error);
          message.error(getUnlockAccountErrorMsg(errorResponse));
        }
      }
    }
    setAutoFocus(false);
    setIsSubmitting(false);
  };

  // get UnlockAccount error Message
  const getUnlockAccountErrorMsg = (errorResponse?: ErrorResponse): string => {
    const errorCode = errorResponse?.errors?.shift();
    let errorMessage: string = LoginHelpMessage.error;
    if (errorResponse?.statusCode === 400) {
      if (errorCode === LoginHelpEmailErrorCode.notFound) {
        // Email does not exist
        errorMessage = LoginHelpMessage.notFound;
      } else if (errorCode === LoginHelpEmailErrorCode.notLocked) {
        // Accounts associated with Email-address are not locked
        errorMessage = LoginHelpMessage.notLocked;
      }
    }
    return errorMessage;
  };

  // get PasswordReset error Message
  const getPasswordResetErrorMsg = (errorResponse?: ErrorResponse): string => {
    const errorCode = errorResponse?.errors?.shift();
    let errorMessage: string = LoginHelpMessage.error;
    if (
      errorResponse?.statusCode === 400 &&
      errorCode === LoginHelpEmailErrorCode.notFound
    ) {
      // Email does not exist
      errorMessage = LoginHelpMessage.notFound;
    }
    return errorMessage;
  };

  const handleChange = (e: RadioChangeEvent) => {
    setSelectValue(e.target.value);
  };

  useEffect(() => {
    return () => {
      // cancel API when unmounted.
      cancelUsersApi();
    };
  }, [cancelUsersApi]);

  return (
    <>
      <Row justify="center" align="middle" className={styles.container}>
        <Col xs={24}>
          <Card bordered={false}>
            <Form
              onFinish={handleFinish}
              form={form}
              values={{
                actionSelection: LoginHelpActionsEnum.passwordReset,
              }}
            >
              <Title level={4} className={commonStyles.mb7}>
                {t("AWVUI-MSG126")}
              </Title>
              <Col
                span={24}
                className={cx(commonStyles.font18, commonStyles.mb3)}
              >
                {t("AWVUI-MSG127")}
              </Col>
              <Item name="actionSelection" form={form} required={false}>
                <Radio.Group onChange={handleChange}>
                  <Space direction="vertical" className={commonStyles.pb16}>
                    <Radio
                      className={commonStyles.font18}
                      value={LoginHelpActionsEnum.passwordReset}
                    >
                      {t("AWVUI-MSG128")}
                    </Radio>
                    <Text className={commonStyles.font14}>
                      {t("AWVUI-MSG129")}
                    </Text>
                    <Radio
                      className={commonStyles.font18}
                      value={LoginHelpActionsEnum.unlockAccount}
                    >
                      {t("AWVUI-MSG130")}
                    </Radio>
                    <Text className={commonStyles.font14}>
                      {t("AWVUI-MSG131")}
                    </Text>
                  </Space>
                </Radio.Group>
              </Item>
              <Col span={24}>
                <Item
                  name="loginEmail"
                  label={t("AWVUI-MSG134")}
                  rules={[
                    {
                      required: true,
                      transform: (value: string) => value?.trim(),
                      message: "Required field",
                    },
                    {
                      type: "email",
                      message: "Email is not valid",
                    },
                  ]}
                  form={form}
                  required={false}
                >
                  <Input
                    placeholder={t("AWVUI-MSG134")}
                    autoFocus={autoFocus}
                  />
                </Item>
              </Col>

              <Row justify="space-between" className={commonStyles.mt4}>
                <Col xs={24} lg={12} order={!isPC ? 1 : 0}>
                  <Item>
                    <Button type="default" onClick={handleBack} block={!isPC}>
                      {t("AWVUI-MSG132")}
                    </Button>
                  </Item>
                </Col>
                <Col xs={!isPC ? 24 : undefined} lg={!isPC ? 12 : undefined}>
                  <Item shouldUpdate>
                    {() => (
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={isSubmitting}
                        block={!isPC}
                        disabled={
                          !form?.isFieldsTouched() ||
                          !!form
                            ?.getFieldsError()
                            .filter(({ errors }) => errors.length).length
                        }
                      >
                        {t("AWVUI-MSG133")}
                      </Button>
                    )}
                  </Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>
      {isSubmitting && <SpinnerDelay />}
    </>
  );
};

export default ResetPasswordScreen;
